import { normalizeCurrencyCode } from '@/src/helpers';
import { useMemo } from 'react';
import { Token, TokenId } from '../models';
import { useAuth } from '@/src/contexts';
import { useAccountLines } from './apis/xrpl/xrpl.queries';
import { useTokens } from './use-tokens';

interface AccountLine {
  currency: string;
  account: string;
  balance: string;
}

/**
 * Custom hook to get the base and quote tokens from the selected pair
 * Automatically creates dynamic tokens for wallet assets not in TOKEN_MAP
 * @param baseTokenId Base token id
 * @param quoteTokenId Quote token id
 * @returns Base and quote tokens (guaranteed to be defined)
 */
export const useSelectedTokens = (
  baseTokenId: TokenId,
  quoteTokenId: TokenId
): [Token | undefined, Token | undefined] => {
  const tokens = useTokens();
  const { user } = useAuth();
  const xrplAddress = user?.xrp_address;

  const { data: accountLines } = useAccountLines({
    account: xrplAddress ?? '',
  });

  return useMemo(() => {
    let baseToken = tokens.find((t) => t.id === baseTokenId);
    let quoteToken = tokens.find((t) => t.id === quoteTokenId);

    // If token not found in TOKEN_MAP, try to create it dynamically from wallet
    if (!baseToken && baseTokenId.endsWith('-dynamic') && accountLines?.lines) {
      (accountLines.lines as AccountLine[]).forEach((line) => {
        const currencyCode = line.currency;
        const issuerAddress = line.account.trim();
        const normalizedCode = normalizeCurrencyCode(currencyCode);
        const dynamicTokenId = `${normalizedCode.toLowerCase()}-${issuerAddress.substring(0, 8)}-dynamic` as TokenId;

        if (dynamicTokenId === baseTokenId) {
          baseToken = {
            id: dynamicTokenId,
            coinGeckoId: 'ripple',
            code: currencyCode,
            issuer: issuerAddress,
            description: normalizedCode,
            icon: '/placeholder-token.svg'
          };
        }
      });
    }

    if (!quoteToken && quoteTokenId.endsWith('-dynamic') && accountLines?.lines) {
      (accountLines.lines as AccountLine[]).forEach((line) => {
        const currencyCode = line.currency;
        const issuerAddress = line.account.trim();
        const normalizedCode = normalizeCurrencyCode(currencyCode);
        const dynamicTokenId = `${normalizedCode.toLowerCase()}-${issuerAddress.substring(0, 8)}-dynamic` as TokenId;

        if (dynamicTokenId === quoteTokenId) {
          quoteToken = {
            id: dynamicTokenId,
            coinGeckoId: 'ripple',
            code: currencyCode,
            issuer: issuerAddress,
            description: normalizedCode,
            icon: '/placeholder-token.svg'
          };
        }
      });
    }

    return [baseToken, quoteToken];
  }, [tokens, baseTokenId, quoteTokenId, accountLines]);
};
